import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import { 
  BookOpen, Clock, Eye, Search, ArrowRight, Calendar, User,
  TrendingUp, Filter, Grid, List, ChevronRight
} from 'lucide-react';
import MegaMenu from '../../components/public/MegaMenu';
import Footer from '../../components/public/Footer';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Categories for filtering
const CATEGORIES = [
  { id: 'all', name: 'All Articles', color: 'white' },
  { id: 'news', name: 'News', color: '#00CC00' },
  { id: 'analysis', name: 'Analysis', color: '#3B82F6' },
  { id: 'opinion', name: 'Opinion', color: '#8B5CF6' },
  { id: 'research', name: 'Research', color: '#F59E0B' },
  { id: 'case-study', name: 'Case Studies', color: '#EF4444' }
];

// Sample articles (fallback when API is unavailable)
const SAMPLE_ARTICLES = [
  {
    id: '1',
    title: 'The Rise of Sports Technology in Africa',
    slug: 'rise-of-sports-technology-africa',
    excerpt: 'How digital platforms are transforming sports management across the continent, from player registration to tournament management.',
    category: 'analysis',
    sport: 'General',
    author: 'John Mwangi',
    published_at: '2026-07-25',
    read_time: '8 min read',
    views: 1250,
    image: 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=800&h=500&fit=crop',
    featured: true
  },
  {
    id: '2',
    title: 'Kenya Golf Union Adopts MyGolfHub Platform',
    slug: 'kenya-golf-union-mygolfhub',
    excerpt: 'The Kenya Golf Union has officially adopted the MyGolfHub platform for handicap tracking and tournament management.',
    category: 'news',
    sport: 'Golf',
    author: 'Sarah Ochieng',
    published_at: '2026-07-20',
    read_time: '4 min read',
    views: 890,
    image: 'https://images.unsplash.com/photo-1587174486073-ae5e5cff23aa?w=800&h=500&fit=crop',
    featured: false
  },
  {
    id: '3',
    title: 'Governance Best Practices for Sports Federations',
    slug: 'governance-best-practices-federations',
    excerpt: 'A comprehensive guide to implementing effective governance frameworks in national sports federations.',
    category: 'research',
    sport: 'General',
    author: 'Dr. Peter Kamau',
    published_at: '2026-07-15',
    read_time: '12 min read',
    views: 650,
    image: 'https://images.unsplash.com/photo-1552664730-d307ca884978?w=800&h=500&fit=crop',
    featured: false
  },
  {
    id: '4',
    title: 'Road to SomaBay 2026: What to Expect',
    slug: 'road-to-somabay-2026-preview',
    excerpt: 'Everything you need to know about the 2026 Road to SomaBay golf circuit and the grand finale in Egypt.',
    category: 'news',
    sport: 'Golf',
    author: 'James Kariuki',
    published_at: '2026-07-10',
    read_time: '6 min read',
    views: 2100,
    image: 'https://images.unsplash.com/photo-1535131749006-b7f58c99034b?w=800&h=500&fit=crop',
    featured: false
  },
  {
    id: '5',
    title: 'The Economics of Sports Sponsorship in East Africa',
    slug: 'economics-sports-sponsorship-east-africa',
    excerpt: 'An in-depth analysis of sponsorship trends, valuations, and opportunities in the East African sports market.',
    category: 'analysis',
    sport: 'General',
    author: 'Mary Wanjiku',
    published_at: '2026-07-05',
    read_time: '10 min read',
    views: 780,
    image: 'https://images.unsplash.com/photo-1560472354-b33ff0c44a43?w=800&h=500&fit=crop',
    featured: false
  },
  {
    id: '6',
    title: 'Case Study: Digitizing Club Licensing in Kenya',
    slug: 'case-study-club-licensing-kenya',
    excerpt: 'How Michezonet helped the Football Kenya Federation implement a comprehensive digital club licensing system.',
    category: 'case-study',
    sport: 'Football',
    author: 'David Ouma',
    published_at: '2026-06-28',
    read_time: '15 min read',
    views: 520,
    image: 'https://images.unsplash.com/photo-1574629810360-7efbbe195018?w=800&h=500&fit=crop',
    featured: false
  }
];

const InsightsPage = () => {
  const [articles, setArticles] = useState(SAMPLE_ARTICLES);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [viewMode, setViewMode] = useState('grid');

  useEffect(() => {
    const fetchContent = async () => {
      try {
        const res = await axios.get(`${API}/cms/insights`);
        if (res.data?.articles?.length > 0) {
          setArticles(res.data.articles);
        }
      } catch (err) {
        // Use sample articles as fallback
      } finally {
        setLoading(false);
      }
    };
    fetchContent();
  }, []);

  const filteredArticles = articles.filter(article => {
    const matchesSearch = article.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         article.excerpt?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = selectedCategory === 'all' || article.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  const featuredArticle = filteredArticles.find(a => a.featured) || filteredArticles[0];
  const regularArticles = filteredArticles.filter(a => a.id !== featuredArticle?.id);

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const getCategoryColor = (categoryId) => {
    const category = CATEGORIES.find(c => c.id === categoryId);
    return category?.color || 'white';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-[#008000]/30 border-t-[#008000] rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black">
      <MegaMenu />
      
      {/* Hero Section */}
      <section className="relative pt-28 pb-16 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#008000]/10 via-black to-black" />
        <div className="absolute inset-0 opacity-5" style={{
          backgroundImage: 'radial-gradient(circle at 1px 1px, rgba(255,255,255,0.3) 1px, transparent 0)',
          backgroundSize: '40px 40px'
        }} />
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <span className="inline-block px-4 py-2 bg-[#008000]/20 border border-[#008000]/30 rounded-full text-[#00CC00] text-sm font-medium mb-6">
              Insights & Research
            </span>
            
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-heading font-bold text-white leading-tight mb-6">
              Sports Industry{' '}
              <span className="bg-gradient-to-r from-[#008000] to-[#00CC00] bg-clip-text text-transparent">
                Intelligence
              </span>
            </h1>
            
            <p className="text-white/70 text-lg md:text-xl max-w-3xl mx-auto">
              Latest news, analysis, research, and insights from Africa's sports industry. 
              Stay informed on trends, opportunities, and developments.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Filters Section */}
      <section className="py-6 bg-gray-900/50 border-y border-white/10 sticky top-16 lg:top-20 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col lg:flex-row gap-4 items-center justify-between">
            {/* Search */}
            <div className="relative flex-1 w-full lg:max-w-md">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-white/40" />
              <input
                type="text"
                placeholder="Search articles..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-12 pr-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white placeholder-white/40 focus:outline-none focus:border-[#008000] transition-colors"
              />
            </div>

            {/* Category Pills */}
            <div className="flex flex-wrap gap-2 justify-center">
              {CATEGORIES.map((category) => (
                <button
                  key={category.id}
                  onClick={() => setSelectedCategory(category.id)}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                    selectedCategory === category.id
                      ? 'bg-[#008000] text-white'
                      : 'bg-white/5 text-white/60 hover:bg-white/10 hover:text-white'
                  }`}
                >
                  {category.name}
                </button>
              ))}
            </div>

            {/* View Toggle */}
            <div className="flex items-center gap-2 bg-white/5 rounded-lg p-1">
              <button
                onClick={() => setViewMode('grid')}
                className={`p-2 rounded ${viewMode === 'grid' ? 'bg-[#008000] text-white' : 'text-white/40 hover:text-white'}`}
              >
                <Grid className="w-5 h-5" />
              </button>
              <button
                onClick={() => setViewMode('list')}
                className={`p-2 rounded ${viewMode === 'list' ? 'bg-[#008000] text-white' : 'text-white/40 hover:text-white'}`}
              >
                <List className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Featured Article */}
      {featuredArticle && (
        <section className="py-12 bg-black">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <Link 
                to={`/insights/${featuredArticle.slug}`}
                className="block group"
              >
                <div className="grid lg:grid-cols-2 gap-8 bg-white/5 border border-white/10 rounded-2xl overflow-hidden hover:border-[#008000]/50 transition-all">
                  <div className="relative h-64 lg:h-auto">
                    <img 
                      src={featuredArticle.image}
                      alt={featuredArticle.title}
                      className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                    <div className="absolute top-4 left-4">
                      <span 
                        className="px-3 py-1 rounded-full text-xs font-bold uppercase"
                        style={{ backgroundColor: getCategoryColor(featuredArticle.category), color: 'black' }}
                      >
                        {featuredArticle.category}
                      </span>
                    </div>
                  </div>
                  <div className="p-8 flex flex-col justify-center">
                    <span className="text-[#00CC00] text-sm font-medium uppercase tracking-wider mb-2">Featured</span>
                    <h2 className="text-2xl lg:text-3xl font-heading font-bold text-white mb-4 group-hover:text-[#008000] transition-colors">
                      {featuredArticle.title}
                    </h2>
                    <p className="text-white/60 mb-6 leading-relaxed">
                      {featuredArticle.excerpt}
                    </p>
                    <div className="flex items-center gap-6 text-sm text-white/40">
                      <span className="flex items-center gap-2">
                        <User className="w-4 h-4" />
                        {featuredArticle.author}
                      </span>
                      <span className="flex items-center gap-2">
                        <Calendar className="w-4 h-4" />
                        {formatDate(featuredArticle.published_at)}
                      </span>
                      <span className="flex items-center gap-2">
                        <Clock className="w-4 h-4" />
                        {featuredArticle.read_time}
                      </span>
                    </div>
                    <div className="mt-6 flex items-center gap-2 text-[#008000] font-medium group-hover:gap-3 transition-all">
                      Read Article <ArrowRight className="w-4 h-4" />
                    </div>
                  </div>
                </div>
              </Link>
            </motion.div>
          </div>
        </section>
      )}

      {/* Articles Grid/List */}
      <section className="py-12 bg-gradient-to-b from-black to-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between mb-8">
            <h2 className="text-2xl font-heading font-bold text-white">
              {selectedCategory === 'all' ? 'Latest Articles' : CATEGORIES.find(c => c.id === selectedCategory)?.name}
            </h2>
            <p className="text-white/40 text-sm">{regularArticles.length} articles</p>
          </div>

          {viewMode === 'grid' ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {regularArticles.map((article, idx) => (
                <motion.div
                  key={article.id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: idx * 0.1 }}
                >
                  <Link 
                    to={`/insights/${article.slug}`}
                    className="block bg-white/5 border border-white/10 rounded-xl overflow-hidden hover:border-[#008000]/50 transition-all group h-full"
                  >
                    <div className="relative h-48">
                      <img 
                        src={article.image}
                        alt={article.title}
                        className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                      <div className="absolute top-4 left-4">
                        <span 
                          className="px-2 py-1 rounded text-xs font-bold uppercase"
                          style={{ backgroundColor: getCategoryColor(article.category), color: 'black' }}
                        >
                          {article.category}
                        </span>
                      </div>
                      {article.sport && article.sport !== 'General' && (
                        <div className="absolute top-4 right-4">
                          <span className="px-2 py-1 bg-black/60 rounded text-xs text-white">
                            {article.sport}
                          </span>
                        </div>
                      )}
                    </div>
                    <div className="p-5">
                      <h3 className="text-lg font-heading font-bold text-white mb-2 line-clamp-2 group-hover:text-[#008000] transition-colors">
                        {article.title}
                      </h3>
                      <p className="text-white/50 text-sm mb-4 line-clamp-2">
                        {article.excerpt}
                      </p>
                      <div className="flex items-center justify-between text-xs text-white/40">
                        <span>{formatDate(article.published_at)}</span>
                        <span className="flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          {article.read_time}
                        </span>
                      </div>
                    </div>
                  </Link>
                </motion.div>
              ))}
            </div>
          ) : (
            <div className="space-y-4">
              {regularArticles.map((article, idx) => (
                <motion.div
                  key={article.id}
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: idx * 0.05 }}
                >
                  <Link 
                    to={`/insights/${article.slug}`}
                    className="flex gap-6 bg-white/5 border border-white/10 rounded-xl p-4 hover:border-[#008000]/50 transition-all group"
                  >
                    <div className="relative w-48 h-32 flex-shrink-0 rounded-lg overflow-hidden hidden sm:block">
                      <img 
                        src={article.image}
                        alt={article.title}
                        className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                    <div className="flex-1 flex flex-col justify-center">
                      <div className="flex items-center gap-3 mb-2">
                        <span 
                          className="px-2 py-0.5 rounded text-xs font-bold uppercase"
                          style={{ backgroundColor: getCategoryColor(article.category), color: 'black' }}
                        >
                          {article.category}
                        </span>
                        {article.sport && article.sport !== 'General' && (
                          <span className="text-white/40 text-xs">{article.sport}</span>
                        )}
                      </div>
                      <h3 className="text-lg font-heading font-bold text-white mb-2 group-hover:text-[#008000] transition-colors">
                        {article.title}
                      </h3>
                      <p className="text-white/50 text-sm mb-3 line-clamp-1">
                        {article.excerpt}
                      </p>
                      <div className="flex items-center gap-4 text-xs text-white/40">
                        <span>{article.author}</span>
                        <span>{formatDate(article.published_at)}</span>
                        <span className="flex items-center gap-1">
                          <Eye className="w-3 h-3" />
                          {article.views}
                        </span>
                      </div>
                    </div>
                    <div className="flex items-center">
                      <ChevronRight className="w-5 h-5 text-white/20 group-hover:text-[#008000] transition-colors" />
                    </div>
                  </Link>
                </motion.div>
              ))}
            </div>
          )}

          {filteredArticles.length === 0 && (
            <div className="text-center py-20">
              <BookOpen className="w-16 h-16 text-white/20 mx-auto mb-4" />
              <h3 className="text-xl font-heading font-bold text-white mb-2">No Articles Found</h3>
              <p className="text-white/50">Try adjusting your search or filter criteria.</p>
            </div>
          )}
        </div>
      </section>

      {/* Newsletter CTA */}
      <section className="py-20 bg-gradient-to-r from-[#008000] to-[#006600] relative overflow-hidden">
        <div className="absolute inset-0 opacity-10" style={{
          backgroundImage: 'radial-gradient(circle at 1px 1px, rgba(255,255,255,0.3) 1px, transparent 0)',
          backgroundSize: '40px 40px'
        }} />
        
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl md:text-4xl font-heading font-bold text-white mb-4">
              Stay Updated
            </h2>
            <p className="text-white/80 text-lg mb-8 max-w-2xl mx-auto">
              Subscribe to our newsletter for the latest sports industry insights, news, and analysis delivered to your inbox.
            </p>
            <form className="flex flex-col sm:flex-row gap-4 max-w-md mx-auto">
              <input
                type="email"
                placeholder="Enter your email"
                className="flex-1 px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-white/50 focus:outline-none focus:border-white/50"
              />
              <button
                type="submit"
                className="px-6 py-3 bg-white text-[#008000] font-semibold rounded-lg hover:bg-gray-100 transition-colors"
              >
                Subscribe
              </button>
            </form>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default InsightsPage;
